import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import Panel from "@/components/ui/Panel";
import Button from "@/components/ui/Button";
import { ButtonLink } from "@/components/ui/Button";
import MarkdownEditor from "@/components/admin/MarkdownEditor";
import { Field, Input, Select, Checkbox } from "@/components/ui/Field";
import { useForm, router } from "@inertiajs/react";
import { AUDIENCE_LABELS, type GuideDraft } from "@/interfaces/guide";
import type { GuideAudience } from "@/interfaces/guide";

export default function AdminGuideEdit({
  guide,
  preview_html,
  audiences,
}: {
  guide: GuideDraft;
  preview_html?: string;
  audiences: GuideAudience[];
}) {
  const isNew = !guide.id;

  const { data, setData, post, patch, processing, errors } = useForm({
    title: guide.title ?? "",
    slug: guide.slug ?? "",
    summary: guide.summary ?? "",
    body: guide.body ?? "",
    audience: guide.audience ?? "organizers",
    position: guide.position ?? 0,
    published: guide.published ?? false,
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (isNew) {
      post("/admin/guides");
    } else {
      patch(`/admin/guides/${guide.id}`);
    }
  }

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          eyebrow="Admin"
          title={isNew ? "New guide" : guide.title || "Edit guide"}
          actions={
            <div className="flex flex-wrap gap-3">
              <ButtonLink href="/admin/guides" variant="secondary">
                All guides
              </ButtonLink>
              {!isNew && guide.published && (
                <ButtonLink href={`/guides/${guide.slug}`} variant="secondary">
                  View live
                </ButtonLink>
              )}
            </div>
          }
        />

        <form onSubmit={submit} className="flex flex-col gap-5">
          <Panel title={isNew ? "new-guide.md" : `${data.slug || "guide"}.md`}>
            <div className="flex flex-col gap-5">
              <Field label="Title" error={errors.title}>
                <Input
                  value={data.title}
                  onChange={(e) => setData("title", e.target.value)}
                  required
                />
              </Field>

              <Field
                label="Slug"
                hint={
                  isNew
                    ? "Leave blank to generate from the title."
                    : "Changing this breaks existing links."
                }
                error={errors.slug}
              >
                <Input
                  value={data.slug}
                  onChange={(e) => setData("slug", e.target.value)}
                  placeholder="organizer-handbook"
                />
              </Field>

              <Field
                label="Summary"
                hint="One line, shown on the guides list."
                error={errors.summary}
              >
                <Input
                  value={data.summary}
                  onChange={(e) => setData("summary", e.target.value)}
                />
              </Field>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="Audience" error={errors.audience}>
                  <Select
                    value={data.audience}
                    onChange={(e) =>
                      setData("audience", e.target.value as GuideAudience)
                    }
                  >
                    {audiences.map((a) => (
                      <option key={a} value={a}>
                        {AUDIENCE_LABELS[a]}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field
                  label="Position"
                  hint="Lower sorts first."
                  error={errors.position}
                >
                  <Input
                    type="number"
                    value={data.position}
                    onChange={(e) =>
                      setData("position", Number(e.target.value))
                    }
                  />
                </Field>
              </div>

              <label className="flex items-center gap-3">
                <Checkbox
                  checked={data.published}
                  onChange={(e) => setData("published", e.target.checked)}
                />
                <span className="font-display text-subheading font-bold">
                  Published
                </span>
              </label>
            </div>
          </Panel>

          <Panel title={`${data.slug || "guide"}.md`}>
            <MarkdownEditor
              value={data.body}
              onChange={(next) => setData("body", next)}
              initialHtml={preview_html}
            />
          </Panel>

          <div className="flex flex-wrap gap-3">
            <Button type="submit" disabled={processing}>
              {processing ? "Saving…" : isNew ? "Create guide" : "Save"}
            </Button>

            {!isNew && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  if (confirm(`Delete "${guide.title}"?`)) {
                    router.delete(`/admin/guides/${guide.id}`);
                  }
                }}
              >
                Delete
              </Button>
            )}
          </div>
        </form>
      </div>
    </Layout>
  );
}
